import { lazy, Suspense, useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/stores/authStore'
import api from '@/api/client'
import ErrorBoundary from '@/components/ErrorBoundary'
import Layout from '@/components/Layout'
import Home from '@/pages/Home'
import Explore from '@/pages/Explore'
import Login from '@/pages/Login'
import Register from '@/pages/Register'
import ForgotPassword from '@/pages/ForgotPassword'
import ResetPassword from '@/pages/ResetPassword'
import VerifyEmail from '@/pages/VerifyEmail'
import Feed from '@/pages/Feed'
import Profile from '@/pages/Profile'
import Messages from '@/pages/Messages'
import Notifications from '@/pages/Notifications'
import Settings from '@/pages/Settings'
import DailyLight from '@/pages/DailyLight'
import SupportMatch from '@/pages/SupportMatch'
import MoodMap from '@/pages/MoodMap'
import Forum from '@/pages/Forum'
import ForumTopic from '@/pages/ForumTopic'
import PostDetail from '@/pages/PostDetail'
import Events from '@/pages/Events'
import EventDetail from '@/pages/EventDetail'
import About from '@/pages/About'
import Search from '@/pages/Search'
import TimeCapsule from '@/pages/TimeCapsule'
import ShoutRoom from '@/pages/ShoutRoom'
import RecoveryStories from '@/pages/RecoveryStories'
import SOSSignals from '@/pages/SOSSignals'
import GratitudeWall from '@/pages/GratitudeWall'
import NotFound from '@/pages/NotFound'
import EphemeralSupport from '@/pages/EphemeralSupport'
import Polls from '@/pages/Polls'
import Hashtag from '@/pages/Hashtag'

// F2.20: Code-split admin/moderator pages with React.lazy + Suspense
const AdminLayout = lazy(() => import('@/pages/admin/Layout'))
const AdminUsers = lazy(() => import('@/pages/admin/Users'))
const AdminPosts = lazy(() => import('@/pages/admin/Posts'))
const AdminForum = lazy(() => import('@/pages/admin/Forum'))
const AdminEvents = lazy(() => import('@/pages/admin/Events'))
const AdminModerators = lazy(() => import('@/pages/admin/Moderators'))
const AdminManagement = lazy(() => import('@/pages/admin/Admins'))
const AdminContact = lazy(() => import('@/pages/admin/Contact'))
const AdminNewsletter = lazy(() => import('@/pages/admin/Newsletter'))
const AdminRecoveryStories = lazy(() => import('@/pages/admin/RecoveryStories'))
const AdminComments = lazy(() => import('@/pages/admin/Comments'))
const AdminMessages = lazy(() => import('@/pages/admin/Messages'))
const AdminSettingsPage = lazy(() => import('@/pages/admin/Settings'))
const AdminReports = lazy(() => import('@/pages/admin/Reports'))
const AdminExperts = lazy(() => import('@/pages/admin/Experts'))
const AdminAIModeration = lazy(() => import('@/pages/admin/AIModeration'))
const AdminLogin = lazy(() => import('@/pages/admin/Login'))
const ModeratorLayout = lazy(() => import('@/pages/moderator/Layout'))
const ModeratorLogin = lazy(() => import('@/pages/moderator/Login'))
const ModUsers = lazy(() => import('@/pages/moderator/Users'))
const ModPosts = lazy(() => import('@/pages/moderator/Posts'))
const ModForum = lazy(() => import('@/pages/moderator/Forum'))
const ModEvents = lazy(() => import('@/pages/moderator/Events'))
const ModComments = lazy(() => import('@/pages/moderator/Comments'))
const ModRecoveryStories = lazy(() => import('@/pages/moderator/RecoveryStories'))
const ModReports = lazy(() => import('@/pages/moderator/Reports'))
const ExpertLayout = lazy(() => import('@/pages/expert/Layout'))
const ExpertEphemeralChats = lazy(() => import('@/pages/expert/EphemeralChats'))
const ExpertEventProposals = lazy(() => import('@/pages/expert/EventProposals'))
const ExpertGuard = lazy(() => import('@/components/ExpertGuard'))
const AdminEventProposals = lazy(() => import('@/pages/admin/EventProposals'))
const ModEventProposals = lazy(() => import('@/pages/moderator/EventProposals'))

const LoadingFallback = () => (
  <div className="flex items-center justify-center min-h-[60vh]">
    <div className="w-8 h-8 rounded-full border-2 border-muted border-t-primary animate-spin" />
  </div>
)

function App() {
  const { isAuthenticated, setAuth, logout } = useAuthStore()
  const { t } = useTranslation()

  // F1.10: Initialize auth state from httpOnly cookie on app load.
  // The cookie is set by the backend on login; we verify it via /auth/me.
  useEffect(() => {
    let cancelled = false
    api.get('/auth/me')
      .then(res => {
        if (!cancelled && res.data?.user) setAuth(res.data.user)
      })
      .catch(() => {
        if (!cancelled) logout()
      })
    return () => { cancelled = true }
  }, [setAuth, logout])

  return (
    <ErrorBoundary>
      {/* F3.A11Y-3: Skip-to-content link for keyboard users */}
      <a href="#main-content" className="skip-link">
        {t('a11y.skipToContent')}
      </a>
      <Routes>
      {/* Main app with navbar/footer */}
      <Route path="/" element={<Layout />}>
        <Route index element={<Home />} />
        <Route path="explore" element={isAuthenticated ? <Explore /> : <Navigate to="/login" />} />
        <Route path="feed" element={isAuthenticated ? <Feed /> : <Navigate to="/login" />} />
        <Route path="search" element={isAuthenticated ? <Search /> : <Navigate to="/login" />} />
        <Route path="post/:id" element={isAuthenticated ? <PostDetail /> : <Navigate to="/login" />} />
        <Route path="forum" element={isAuthenticated ? <Forum /> : <Navigate to="/login" />} />
        <Route path="forum/:id" element={isAuthenticated ? <ForumTopic /> : <Navigate to="/login" />} />
        <Route path="messages" element={isAuthenticated ? <Messages /> : <Navigate to="/login" />} />
        <Route path="messages/:userId" element={isAuthenticated ? <Messages /> : <Navigate to="/login" />} />
        <Route path="notifications" element={isAuthenticated ? <Notifications /> : <Navigate to="/login" />} />
        <Route path="profile/:username" element={isAuthenticated ? <Profile /> : <Navigate to="/login" />} />
        <Route path="settings" element={isAuthenticated ? <Settings /> : <Navigate to="/login" />} />
        <Route path="daily-light" element={isAuthenticated ? <DailyLight /> : <Navigate to="/login" />} />
        <Route path="support" element={isAuthenticated ? <SupportMatch /> : <Navigate to="/login" />} />
        <Route path="mood-map" element={isAuthenticated ? <MoodMap /> : <Navigate to="/login" />} />
        <Route path="events" element={isAuthenticated ? <Events /> : <Navigate to="/login" />} />
        <Route path="events/:id" element={isAuthenticated ? <EventDetail /> : <Navigate to="/login" />} />
        <Route path="polls" element={isAuthenticated ? <Polls /> : <Navigate to="/login" />} />
        <Route path="time-capsule" element={isAuthenticated ? <TimeCapsule /> : <Navigate to="/login" />} />
        <Route path="shout-room" element={isAuthenticated ? <ShoutRoom /> : <Navigate to="/login" />} />
        <Route path="recovery-stories" element={isAuthenticated ? <RecoveryStories /> : <Navigate to="/login" />} />
        <Route path="sos" element={isAuthenticated ? <SOSSignals /> : <Navigate to="/login" />} />
        <Route path="ephemeral-support" element={isAuthenticated ? <EphemeralSupport /> : <Navigate to="/login" />} />
        <Route path="ephemeral-support/:id" element={isAuthenticated ? <EphemeralSupport /> : <Navigate to="/login" />} />
        <Route path="gratitude" element={isAuthenticated ? <GratitudeWall /> : <Navigate to="/login" />} />
        <Route path="tag/:tag" element={isAuthenticated ? <Hashtag /> : <Navigate to="/login" />} />
        <Route path="about" element={<About />} />
        <Route path="login" element={!isAuthenticated ? <Login /> : <Navigate to="/feed" />} />
        <Route path="register" element={!isAuthenticated ? <Register /> : <Navigate to="/feed" />} />
        <Route path="forgot-password" element={<ForgotPassword />} />
        <Route path="reset-password" element={<ResetPassword />} />
        <Route path="verify-email" element={<VerifyEmail />} />
        <Route path="*" element={<NotFound />} />
      </Route>

      {/* Admin routes - F2.20: code-split with Suspense */}
      <Route path="/admin/login" element={<Suspense fallback={<LoadingFallback />}><AdminLogin /></Suspense>} />
      <Route path="/admin" element={<Suspense fallback={<LoadingFallback />}><AdminLayout /></Suspense>}>
        <Route index element={<div />} />
        <Route path="users" element={<Suspense fallback={<LoadingFallback />}><AdminUsers /></Suspense>} />
        <Route path="posts" element={<Suspense fallback={<LoadingFallback />}><AdminPosts /></Suspense>} />
        <Route path="forum" element={<Suspense fallback={<LoadingFallback />}><AdminForum /></Suspense>} />
        <Route path="events" element={<Suspense fallback={<LoadingFallback />}><AdminEvents /></Suspense>} />
        <Route path="event-proposals" element={<Suspense fallback={<LoadingFallback />}><AdminEventProposals /></Suspense>} />
        <Route path="moderators" element={<Suspense fallback={<LoadingFallback />}><AdminModerators /></Suspense>} />
        <Route path="admins" element={<Suspense fallback={<LoadingFallback />}><AdminManagement /></Suspense>} />
        <Route path="contact" element={<Suspense fallback={<LoadingFallback />}><AdminContact /></Suspense>} />
        <Route path="newsletter" element={<Suspense fallback={<LoadingFallback />}><AdminNewsletter /></Suspense>} />
        <Route path="recovery-stories" element={<Suspense fallback={<LoadingFallback />}><AdminRecoveryStories /></Suspense>} />
        <Route path="comments" element={<Suspense fallback={<LoadingFallback />}><AdminComments /></Suspense>} />
        <Route path="messages" element={<Suspense fallback={<LoadingFallback />}><AdminMessages /></Suspense>} />
        <Route path="settings" element={<Suspense fallback={<LoadingFallback />}><AdminSettingsPage /></Suspense>} />
        <Route path="reports" element={<Suspense fallback={<LoadingFallback />}><AdminReports /></Suspense>} />
        <Route path="experts" element={<Suspense fallback={<LoadingFallback />}><AdminExperts /></Suspense>} />
        <Route path="ai-moderation" element={<Suspense fallback={<LoadingFallback />}><AdminAIModeration /></Suspense>} />
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Route>

      {/* Moderator routes - F2.20: code-split with Suspense */}
      <Route path="/moderator/login" element={<Suspense fallback={<LoadingFallback />}><ModeratorLogin /></Suspense>} />
      <Route path="/moderator" element={<Suspense fallback={<LoadingFallback />}><ModeratorLayout /></Suspense>}>
        <Route index element={<div />} />
        <Route path="users" element={<Suspense fallback={<LoadingFallback />}><ModUsers /></Suspense>} />
        <Route path="posts" element={<Suspense fallback={<LoadingFallback />}><ModPosts /></Suspense>} />
        <Route path="forum" element={<Suspense fallback={<LoadingFallback />}><ModForum /></Suspense>} />
        <Route path="events" element={<Suspense fallback={<LoadingFallback />}><ModEvents /></Suspense>} />
        <Route path="event-proposals" element={<Suspense fallback={<LoadingFallback />}><ModEventProposals /></Suspense>} />
        <Route path="comments" element={<Suspense fallback={<LoadingFallback />}><ModComments /></Suspense>} />
        <Route path="recovery-stories" element={<Suspense fallback={<LoadingFallback />}><ModRecoveryStories /></Suspense>} />
        <Route path="reports" element={<Suspense fallback={<LoadingFallback />}><ModReports /></Suspense>} />
        <Route path="*" element={<Navigate to="/moderator" replace />} />
      </Route>

      {/* Expert routes - F2.20: code-split with Suspense */}
      <Route path="/expert" element={<Suspense fallback={<LoadingFallback />}><ExpertGuard><ExpertLayout /></ExpertGuard></Suspense>}>
        <Route index element={<Suspense fallback={<LoadingFallback />}><ExpertEphemeralChats /></Suspense>} />
        <Route path="event-proposals" element={<Suspense fallback={<LoadingFallback />}><ExpertEventProposals /></Suspense>} />
        <Route path=":id" element={<Suspense fallback={<LoadingFallback />}><ExpertEphemeralChats /></Suspense>} />
        <Route path="*" element={<Navigate to="/expert" replace />} />
      </Route>
    </Routes>
    </ErrorBoundary>
  )
}

export default App
